import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon, CalendarIcon } from 'lucide-react';
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    q: 'Does laser therapy hurt?',
    a: 'No, laser therapy is generally painless. Most patients experience a mild, soothing warmth over the treated area. There is no cutting, burning, or tissue damage involved.'
  }, {
    q: 'How long does a session take?',
    a: 'Sessions are typically very fast. Depending on the size of the area being treated and the specific condition, a single treatment usually takes between 3 to 12 minutes.'
  }, {
    q: 'How many treatments will I need?',
    a: 'This depends entirely on your condition. Acute conditions may require only 4 to 6 sessions, while chronic conditions often require 6 to 12 or more sessions. Your provider will create a personalized plan for you.'
  }, {
    q: 'When can I return to normal activity?',
    a: 'Most patients can return to normal daily activities immediately. However, if you are treating a sports injury or severe pain, your provider may advise you to avoid heavy overexertion for 24 to 48 hours to allow the tissue to heal.'
  }, {
    q: 'Will insurance cover treatment?',
    a: 'Laser therapy is generally considered an elective, out-of-pocket treatment and is not typically covered by standard health insurance. We offer various payment options and packages to make care accessible.'
  }, {
    q: 'How is Class IV laser therapy different from cold laser therapy?',
    a: 'Class IV lasers deliver a higher wattage of power compared to Class III (cold) lasers. This allows the light energy to penetrate deeper into the tissue and deliver a therapeutic dose of energy in a much shorter amount of time.'
  }, {
    q: 'What does treatment feel like?',
    a: 'Patients usually feel a gentle, soothing warmth as the laser handpiece is moved over the skin. Many find the experience highly relaxing, similar to a warm massage.'
  }, {
    q: 'When might I notice results?',
    a: 'Some patients notice immediate pain relief and improved mobility after their very first session. For others, especially those with chronic conditions, it may take 3 to 4 sessions before significant improvement is felt. The effects are cumulative.'
  }];
  return <section className="py-20 md:py-28 bg-white" data-id="element-46">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-47">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-48">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy mb-6" data-id="element-49">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600" data-id="element-50">
            Find answers to common questions about Class IV laser therapy.
          </p>
        </motion.div>

        <div className="space-y-4 mb-16" data-id="element-51">
          {faqs.map((faq, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: idx * 0.05
        }} className="border border-gray-200 rounded-xl overflow-hidden bg-brand-gray-light" data-id="element-52">
              <button onClick={() => setOpenIndex(openIndex === idx ? null : idx)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-53">
                <span className="text-lg font-bold text-brand-navy pr-4" data-id="element-54">
                  {faq.q}
                </span>
                {openIndex === idx ? <ChevronUpIcon className="w-6 h-6 text-brand-teal flex-shrink-0" data-id="element-55" /> : <ChevronDownIcon className="w-6 h-6 text-gray-400 flex-shrink-0" data-id="element-56" />}
              </button>

              <AnimatePresence data-id="element-57">
                {openIndex === idx && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-58">
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-59">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-60">
          <p className="text-gray-600 mb-6" data-id="element-61">
            Still have questions? We're here to help.
          </p>
          <button className="px-8 py-4 bg-brand-teal hover:bg-brand-teal-dark text-white rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg shadow-brand-teal/25" data-id="element-62">
            <CalendarIcon className="w-5 h-5" data-id="element-63" />
            Schedule a Consultation
          </button>
        </motion.div>
      </div>
    </section>;
}